/**
 * ScheduledScreenshotWatcher
 *
 * Periodically captures screenshots from Playjector while the game is idle
 * (between macro-walk completion and player join), compares the perceptual
 * hash against the campaign's reference image, and emits a mismatch event
 * that PavilionCoordinator consumes to trigger a clean game restart.
 *
 * State machine:
 *   idle → active   (gameReady + watcher enabled + restartOnDisconnect ok)
 *   active → paused (player joins / stream starts)
 *   paused → idle   (stream stops with restartOnDisconnect=false, or game closed)
 *   active → idle   (game closed / campaign cleared / mismatch detected)
 *
 * Architecture: Coordinator pattern — DI constructor, event subscriptions,
 * public events, private field state.
 */

import { CoordinatorEventImpl } from '../_types/events';
import type { IPlayjectorService } from '../../services/playjector/interfaces/IPlayjectorService';
import type { GameLaunchCoordinator } from '../game-launch/GameLaunchCoordinator';
import type { PavilionCoordinator } from '../pavilion/PavilionCoordinator';
import type { SessionCoordinator } from '../session/SessionCoordinator';
import type { WatcherMismatchEvent, WatcherCheckPassedEvent } from './types';
import { hammingDistance } from './helpers';
import * as Sentry from '@sentry/react';

export interface ScheduledScreenshotWatcherDeps {
  playjectorService: IPlayjectorService;
  gameLaunchCoordinator: GameLaunchCoordinator;
  pavilionCoordinator: PavilionCoordinator;
  sessionCoordinator: SessionCoordinator;
}

export class ScheduledScreenshotWatcher {
  private readonly playjectorService: IPlayjectorService;
  private readonly gameLaunchCoordinator: GameLaunchCoordinator;
  private readonly pavilionCoordinator: PavilionCoordinator;
  private readonly sessionCoordinator: SessionCoordinator;

  private active = false;
  private checkInFlight = false;
  private campaignId: string | null = null;
  private lastCheckTimestamp: number | null = null;
  private lastCheckResult: 'pass' | 'mismatch' | null = null;
  private checksPerformed = 0;

  private readonly disposers: Array<() => void> = [];
  private tickTimeout: ReturnType<typeof setTimeout> | null = null;
  private initialized = false;

  public events = {
    watcherMismatchDetected: new CoordinatorEventImpl<WatcherMismatchEvent>(),
    watcherCheckPassed: new CoordinatorEventImpl<WatcherCheckPassedEvent>(),
    watcherStarted: new CoordinatorEventImpl<{ campaignId: string }>(),
    watcherStopped: new CoordinatorEventImpl<{ reason: string }>(),
  };

  constructor(deps: ScheduledScreenshotWatcherDeps) {
    this.playjectorService = deps.playjectorService;
    this.gameLaunchCoordinator = deps.gameLaunchCoordinator;
    this.pavilionCoordinator = deps.pavilionCoordinator;
    this.sessionCoordinator = deps.sessionCoordinator;
  }

  /** Wire up event subscriptions. Idempotent. */
  public init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.disposers.push(
      this.gameLaunchCoordinator.events.gameReady.addListener(() => {
        this.handleGameReady();
      }),
    );

    this.disposers.push(
      this.gameLaunchCoordinator.events.gameClosed.addListener(() => {
        this.deactivate('game_closed');
      }),
    );

    this.disposers.push(
      this.sessionCoordinator.events.streamStarted.addListener(() => {
        this.pause();
      }),
    );

    this.disposers.push(
      this.sessionCoordinator.events.streamStopped.addListener(() => {
        this.handleStreamStopped();
      }),
    );
  }

  /** Tear down for tests */
  public destroy(): void {
    this.deactivate('destroyed');
    this.disposers.forEach((d) => d());
    this.disposers.length = 0;
    this.initialized = false;
  }

  // ==========================================================================
  // Event Handlers
  // ==========================================================================

  private handleGameReady(): void {
    const config = this.pavilionCoordinator.getCampaignConfig();
    if (!config) return;

    if (!config.scheduledWatcher?.enabled) return;

    // Watcher only makes sense when restart-on-disconnect is enabled.
    // Without it, post-player game state is non-deterministic.
    if (config.restartOnDisconnect === false) return;

    // If a stream is already active (player joined instantly), skip activation.
    if (this.sessionCoordinator.getStatus() === 'hosting') return;

    this.activate(config.campaignId);
  }

  private handleStreamStopped(): void {
    const config = this.pavilionCoordinator.getCampaignConfig();

    // If restartOnDisconnect is true, the game will restart → macro walk →
    // gameReady will fire → watcher re-activates naturally. Just deactivate.
    // If restartOnDisconnect is false, game stays running but state is
    // non-deterministic — stay idle.
    this.deactivate('stream_stopped');
  }

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  private activate(campaignId: string): void {
    if (this.active) return;

    this.active = true;
    this.campaignId = campaignId;
    this.checksPerformed = 0;
    this.lastCheckResult = null;
    this.lastCheckTimestamp = null;
    this.checkInFlight = false;

    console.log(`[ScheduledScreenshotWatcher] ▶ Activated for campaign ${campaignId}`);
    this.events.watcherStarted.emit({ campaignId });
    this.scheduleNextTick();
  }

  private pause(): void {
    if (!this.active) return;
    this.clearTick();
    this.active = false;
    console.log('[ScheduledScreenshotWatcher] ⏸ Paused (player joined)');
    this.events.watcherStopped.emit({ reason: 'player_joined' });
  }

  private deactivate(reason: string): void {
    if (!this.active) return;
    this.clearTick();
    this.active = false;
    this.checkInFlight = false;
    this.campaignId = null;
    console.log(`[ScheduledScreenshotWatcher] ⏹ Deactivated: ${reason}`);
    this.events.watcherStopped.emit({ reason });
  }

  // ==========================================================================
  // Tick / Check Loop
  // ==========================================================================

  private scheduleNextTick(): void {
    const config = this.pavilionCoordinator.getCampaignConfig();
    const intervalMs = config?.scheduledWatcher?.intervalMs ?? 300_000;
    this.tickTimeout = setTimeout(() => this.tick(), intervalMs);
  }

  private clearTick(): void {
    if (this.tickTimeout !== null) {
      clearTimeout(this.tickTimeout);
      this.tickTimeout = null;
    }
  }

  private async tick(): Promise<void> {
    if (!this.active) return;

    const config = this.pavilionCoordinator.getCampaignConfig();
    if (!config?.scheduledWatcher) {
      this.deactivate('config_removed');
      return;
    }

    const { referenceImageHash, captureWidth, captureHeight, hashTolerance } =
      config.scheduledWatcher;

    this.checkInFlight = true;

    try {
      const result = await this.playjectorService.requestScheduledScreenshot({
        width: captureWidth ?? 1920,
        height: captureHeight ?? 1080,
      });

      if (!this.active) return;

      const distance = hammingDistance(result.hash, referenceImageHash);
      const tolerance = hashTolerance ?? 20;

      this.checkInFlight = false;
      this.lastCheckTimestamp = Date.now();
      this.checksPerformed += 1;

      if (distance <= tolerance) {
        this.lastCheckResult = 'pass';
        this.events.watcherCheckPassed.emit({
          campaignId: config.campaignId,
          hammingDistance: distance,
        });
        this.scheduleNextTick();
      } else {
        this.lastCheckResult = 'mismatch';
        Sentry.logger.warn(
          '[ScheduledScreenshotWatcher] Mismatch detected',
          { campaignId: config.campaignId, distance, tolerance, referenceImageHash, actualHash: result.hash },
        );
        this.events.watcherMismatchDetected.emit({
          campaignId: config.campaignId,
          expectedHash: referenceImageHash,
          actualHash: result.hash,
          hammingDistance: distance,
          tolerance,
        });
        this.deactivate('mismatch_detected');
      }
    } catch (error) {
      if (!this.active) return;

      console.warn('[ScheduledScreenshotWatcher] Screenshot request failed:', error);
      this.checkInFlight = false;
      this.scheduleNextTick();
    }
  }
}
